import type {
  AssessmentSectionProgress,
  QuestionSectionDefinition,
} from "../../types/assessment";

/**
 * Presentation-only grouping for the assessments that render one flat question
 * list. The questions themselves are neither reordered nor filtered — the
 * groups exist so the progress sidebar can report what is answered and what is
 * still open per theme.
 */

export const EXTPRS_SECTIONS: QuestionSectionDefinition[] = [
  {
    key: "physical",
    labelEn: "Physical readiness",
    labelDe: "Körperliche Leistungsfähigkeit",
    codes: ["P1", "P2", "P3", "P4"],
  },
  {
    key: "household",
    labelEn: "Household preparedness",
    labelDe: "Häusliche Notfallvorsorge",
    codes: ["H1", "H2", "H3", "H4", "H5", "H6", "H7", "H8", "H9"],
  },
  {
    key: "cognitive",
    labelEn: "Cognitive readiness",
    labelDe: "Kognitive / Bedenkliche Bereitschaft",
    codes: ["C1", "C2"],
  },
  {
    key: "behaviour",
    labelEn: "Behaviour & continuity",
    labelDe: "Verhalten & Kontinuität",
    codes: ["B1", "B2", "B3", "B4", "B5", "B6", "B7", "B8", "B9"],
  },
  {
    key: "power",
    labelEn: "Power & blackout",
    labelDe: "Strom & Blackout",
    codes: ["Q31", "Q32", "Q33"],
  },
  {
    key: "evacuation",
    labelEn: "Evacuation & documents",
    labelDe: "Evakuierung & Dokumente",
    codes: ["Q34", "Q35", "Q36", "Q37"],
  },
  {
    key: "medical",
    labelEn: "Medical & first aid",
    labelDe: "Medizin & Erste Hilfe",
    codes: ["Q38", "Q39", "Q40"],
  },
  {
    key: "supplies",
    labelEn: "Food, water & cash",
    labelDe: "Lebensmittel, Wasser & Bargeld",
    codes: ["Q41", "Q42", "Q43"],
  },
  {
    key: "communication",
    labelEn: "Communication & family",
    labelDe: "Kommunikation & Familie",
    codes: ["Q44", "Q45"],
  },
];

export const EKS_SECTIONS: QuestionSectionDefinition[] = [
  {
    key: "cohesion_and_trust",
    labelEn: "Cohesion & trust",
    labelDe: "Zusammenhalt & Vertrauen",
    codes: ["E1", "E2", "E10"],
  },
  {
    key: "mission_alignment",
    labelEn: "Mission alignment & retention",
    labelDe: "Auftragsverständnis & Bindung",
    codes: ["E5", "E6", "E11"],
  },
  {
    key: "reporting_culture_and_security",
    labelEn: "Reporting culture & security",
    labelDe: "Meldekultur & Sicherheit",
    codes: ["E4", "E7", "E8", "E9"],
  },
  {
    key: "training_and_readiness",
    labelEn: "Training & operational readiness",
    labelDe: "Training & Einsatzbereitschaft",
    codes: ["E3"],
  },
  {
    key: "awareness",
    labelEn: "Awareness & briefing",
    labelDe: "Wahrnehmung & Unterweisung",
    codes: ["EQ1", "EQ2"],
  },
  {
    key: "continuity",
    labelEn: "Continuity & cover",
    labelDe: "Kontinuität & Vertretung",
    codes: ["EQ3", "EQ4"],
  },
  {
    key: "community",
    labelEn: "Community & contacts",
    labelDe: "Gemeinschaft & Kontakte",
    codes: ["EQ5", "EQ6"],
  },
  {
    key: "household",
    labelEn: "Home self-sufficiency",
    labelDe: "Autarkie zu Hause",
    codes: ["EQ7", "EQ8"],
  },
  {
    key: "shelter",
    labelEn: "Evacuation & shelter",
    labelDe: "Evakuierung & Schutzräume",
    codes: ["EQ9", "EQ10"],
  },
];

export function buildQuestionSections<T>(params: {
  definitions: QuestionSectionDefinition[];
  questions: T[];
  lang: "en" | "de";
  getCode: (question: T) => string | null;
  isAnswered: (question: T) => boolean;
  isSkipped: (question: T) => boolean;
  fallbackLabelEn: string;
  fallbackLabelDe: string;
}): AssessmentSectionProgress[] {
  const {
    definitions,
    questions,
    lang,
    getCode,
    isAnswered,
    isSkipped,
    fallbackLabelEn,
    fallbackLabelDe,
  } = params;

  const known = new Set(definitions.flatMap((definition) => definition.codes));

  const tally = (bucket: T[]) => ({
    answered: bucket.filter((question) => isAnswered(question)).length,
    skipped: bucket.filter((question) => !isAnswered(question) && isSkipped(question)).length,
    total: bucket.length,
  });

  const sections = definitions
    .map((definition) => {
      const bucket = questions.filter((question) => {
        const code = getCode(question);
        return Boolean(code) && definition.codes.includes(code as string);
      });

      return {
        key: definition.key,
        label: lang === "de" ? definition.labelDe : definition.labelEn,
        ...tally(bucket),
      };
    })
    .filter((section) => section.total > 0);

  const leftovers = questions.filter((question) => {
    const code = getCode(question);
    return !code || !known.has(code);
  });

  if (leftovers.length > 0) {
    sections.push({
      key: "other",
      label: lang === "de" ? fallbackLabelDe : fallbackLabelEn,
      ...tally(leftovers),
    });
  }

  return sections;
}
